import { FC, useMemo } from 'react';

import { AnalyticsStackedTimeSeriesBox } from 'components/AnalyticsBox/AnalyticsStackedTimeSeriesBox/AnalyticsStackedTimeSeriesBox';
import { formatMilliseconds } from 'components/AnalyticsBox/AnalyticsStackedTimeSeriesBox/AnalyticsStackedTimeSeriesBox.utils';

import { IApiPerformanceApiMetric } from '../AnalyticsApiPerformanceTab.types';
import {
  buildApiPerformanceSegments,
  formatTooltipTitle,
  formatXAxisTick,
  mapTimeSeriesPointToStackedPoint,
  PRIMARY_METRIC_LABEL,
  PRIMARY_METRIC_STATISTIC_LABEL,
  resolveApiMetricDisplayName,
  resolveIndicatorPrimaryValue,
  X_AXIS_TICK_ROTATION_DEGREES
} from '../AnalyticsApiPerformanceTab.utils';

interface IApiPerformanceCardProps {
  apiMetric: IApiPerformanceApiMetric;
  isLoading: boolean;
}

export const ApiPerformanceCard: FC<IApiPerformanceCardProps> = ({
  apiMetric,
  isLoading
}) => {
  const segments = useMemo(
    () => buildApiPerformanceSegments(apiMetric),
    [apiMetric]
  );

  const hasIndicatorData = apiMetric.indicator.p95TotalDurationMs !== null;

  const timeSeries = useMemo(() => {
    if (!hasIndicatorData) {
      return [];
    }
    return apiMetric.timeSeries.map(mapTimeSeriesPointToStackedPoint);
  }, [apiMetric.timeSeries, hasIndicatorData]);

  const primaryMetric = useMemo(
    () => ({
      label: PRIMARY_METRIC_LABEL,
      value: resolveIndicatorPrimaryValue(apiMetric),
      statisticLabel: PRIMARY_METRIC_STATISTIC_LABEL
    }),
    [apiMetric]
  );

  const apiMetricDisplayName = useMemo(
    () => resolveApiMetricDisplayName(apiMetric),
    [apiMetric]
  );

  return (
    <AnalyticsStackedTimeSeriesBox
      isLoading={isLoading}
      title={apiMetricDisplayName}
      primaryMetric={primaryMetric}
      segments={segments}
      timeSeries={timeSeries}
      valueFormatter={formatMilliseconds}
      xAxisTickFormatter={formatXAxisTick}
      xAxisTickAngle={X_AXIS_TICK_ROTATION_DEGREES}
      tooltipTitleFormatter={formatTooltipTitle}
    />
  );
};
